import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';

const BalanceSheet = () => {
  const [asOf, setAsOf] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  const fetchBalanceSheet = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (asOf) params.append('asOf', asOf);
      
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${baseURL}/accounting-entries/balance-sheet?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const result = await res.json();
      setData(result.data);
    } catch (err) {
      setError('Failed to fetch balance sheet.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set default date to today
    const today = new Date().toISOString().split('T')[0];
    setAsOf(today);
  }, []);

  useEffect(() => {
    if (asOf) {
      fetchBalanceSheet();
    }
  }, [asOf]);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <GirviPageShell
      title="Balance Sheet"
      subtitle="Transactions · Statements"
      chipLabel="Transactions"
      maxWidth="xl"
     compact>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        {user && (
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>{user.name}</Typography>
            <Typography variant="h6" color="textSecondary">{user.companyName}</Typography>
            <Typography variant="body2">{user.address}</Typography>
            <Typography variant="body2">Mobile: {user.mobileNumber}</Typography>
            <Typography variant="body2">License: {user.licenseNumber}</Typography>
          </>
        )}
        <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
          {asOf ? `As of ${formatDate(asOf)}` : 'Select date'}
        </Typography>
      </Box>

      {/* Date Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
        <TextField
          label="As of Date"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          onClick={fetchBalanceSheet}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Generate Report
        </Button>
        <Button
          variant="outlined"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
        >
          Print
        </Button>
      </Box>
      <Typography variant="caption" color="textSecondary" display="block" sx={{ textAlign: 'center', mb: 2, maxWidth: 560, mx: 'auto' }}>
        Includes opening balances and accounting entries dated on or before the &quot;As of&quot; date. Later-dated transactions (for example pledge release the next day) appear only when you set this date to that day or later.
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {data && (
        <Grid container spacing={3}>
          {/* Assets Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'blue' }}>
                  Assets
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ledger Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.assets.map((group, gIdx) => (
                        <React.Fragment key={group.groupCode}>
                          {group.ledgers.map((ledger, lIdx) => (
                            <TableRow key={lIdx}>
                              <TableCell>{lIdx === 0 ? `${group.groupCode} - ${group.groupName}` : ''}</TableCell>
                              <TableCell>{ledger.name}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(ledger.balance)}</TableCell>
                            </TableRow>
                          ))}
                          {/* Group total row */}
                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total {group.groupName}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'blue' }}>{formatCurrency(group.groupTotal)}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                      <TableRow sx={{ backgroundColor: 'grey.200' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total Assets</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'blue' }}>{formatCurrency(data.totalAssets)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Liabilities Section */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'red' }}>
                  Liabilities
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ledger Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.liabilities.map((group, gIdx) => (
                        <React.Fragment key={group.groupCode}>
                          {group.ledgers.map((ledger, lIdx) => (
                            <TableRow key={lIdx}>
                              <TableCell>{lIdx === 0 ? `${group.groupCode} - ${group.groupName}` : ''}</TableCell>
                              <TableCell>{ledger.name}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(ledger.balance)}</TableCell>
                            </TableRow>
                          ))}
                          {/* Group total row */}
                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total {group.groupName}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'red' }}>{formatCurrency(group.groupTotal)}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                      <TableRow sx={{ backgroundColor: 'grey.200' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Total Liabilities</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'red' }}>{formatCurrency(data.totalLiabilities)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Net Profit */}
          <Grid item xs={12}>
            <Card elevation={3} sx={{ backgroundColor: data.netProfit >= 0 ? 'success.light' : 'error.light', color: 'white' }}>
              <CardContent>
                <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>
                  Net Profit
                </Typography>
                <Typography variant="h4" align="center" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(data.netProfit)}
                </Typography>
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  (Total Assets - Total Liabilities)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Position Summary */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Financial Position Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Total Assets</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      {formatCurrency(data.totalAssets)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Total Liabilities</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'red' }}>
                      {formatCurrency(data.totalLiabilities)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Net Profit</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: data.netProfit >= 0 ? 'green' : 'red' }}>
                      {formatCurrency(data.netProfit)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {!data && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Select a date and generate the balance sheet
          </Typography>
        </Box>
      )}
    </GirviPageShell>
  );
};

export default BalanceSheet; 